import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import type { EmergencyCall } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import { setSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { isValidInternationalPhoneNumber } from '@rsos/utils/internationalPhoneNumberUtils';
import { fetchLocation, removeNumFromQueue } from '../../../../actions';
import { FETCH_LOCATION_SUCCESS } from '../../../../actions/actionTypes';
import type { RootState } from '../../../../app';
import callQueueSelectorNext from '../../../../selectors/callQueueSelectorNext';
import { selectProductToken } from '../../../../utils/getProductToken';

const onlyDigits = (s: string): string => s.replace(/\D/g, '');

/**
 * Normalize a USA phone-number search term to the leading-`1`, 11-
 * digit caller-ID form used by the LEI lookup.
 *
 * Uses `isValidInternationalPhoneNumber` (libphonenumber-js, defaulting
 * to US) to validate the term. Accepts US and international numbers:
 *
 *   - `1-222-333-4444`
 *   - `12223334444`
 *   - `2223334444`
 *   - `222-333-4444`
 *   - international numbers (e.g. `+44...`)
 *
 * Per product direction the LEI fetch always runs against the
 * leading-`1` form, so a 10-digit input is widened to 11 digits
 * before the fetch fires. The user's `searchTerm` is left untouched
 * — only the value sent on the wire is normalized.
 *
 * Returns `null` when the term isn't a recognizable phone number.
 */
export function parseLeiTerm(term: string): string | null {
  const trimmed = term.trim();
  if (!isValidInternationalPhoneNumber(trimmed)) return null;
  const digits = onlyDigits(trimmed);
  if (!digits) return null;
  return digits.startsWith('1') ? digits : `1${digits}`;
}

/**
 * Decide whether the LEI lookup should be skipped because the
 * caller is already represented in the active call queue. We treat
 * an exact key match, a queue key whose digits include the LEI
 * digits (e.g. queue `15551234567` vs LEI `5551234567`), or a queue
 * key whose digits are a substring of the LEI digits as a hit. This
 * mirrors the substring matcher in `runCallsSearch`/`call.ts` so the
 * "regular call-result row will handle this" hand-off lines up with
 * what the user actually sees in the dropdown.
 */
export function isCallerInQueue(
  leiCallerID: string,
  callQueue: Record<string, unknown> | undefined,
): boolean {
  if (!callQueue) return false;
  if (callQueue[leiCallerID]) return true;
  const digits = onlyDigits(leiCallerID);
  if (!digits) return false;
  for (const key of Object.keys(callQueue)) {
    const keyDigits = onlyDigits(key);
    if (!keyDigits) continue;
    if (keyDigits === digits) return true;
    if (keyDigits.includes(digits) || digits.includes(keyDigits)) return true;
  }
  return false;
}

interface UseLeiSearch {
  /** See {@link parseLeiTerm}. */
  parse: (term: string) => string | null;
  /**
   * Fire the legacy LEI location pull (`/v1/rsos/location?caller_id=`)
   * for `callerID` and select the caller in the queue once the fetch
   * resolves successfully. Mirrors the legacy
   * `ConnectedUniversalSearch.searchLocation` happy path, minus the
   * TF911 / collaborator / GIS-error toasts — the map search bar has
   * its own ambient surfaces for those and we don't want to double-
   * fire them.
   *
   * No-op when no product token is available (the bar shouldn't
   * raise an error toast for typing while logged-out / unscoped); the
   * parallel RAD-e (`useAdrSearch`) lookup still runs.
   */
  kickoff: (callerID: string) => Promise<void>;
}

// `fetchLocation` is a JS thunk that resolves with either
// `{ action, requestID, payloadID }` (success) or the action object
// returned by `fetchLocationFailed` (failure). The shape isn't
// expressible against the JS thunk signature, so we narrow with a
// single boundary cast at the dispatch site.
type LegacyDispatch = (action: unknown) => Promise<
  | {
      action?: { type?: string };
    }
  | undefined
>;

/**
 * Decide whether a call queue entry represents a real, in-flight 911
 * interaction worth keeping in the queue (and surfacing as "Active
 * 911 Call" in the search dropdown), as opposed to a stale shell
 * created by a manual LEI lookup that produced no usable data.
 *
 * "Active" means at least one of:
 *
 *   - a primary location pulled from LEI / ALI / RAD-e
 *   - a secondary location (RAD-e supplemental, etc.)
 *   - an in-flight TF911 / agency-share conversation
 *   - a collaborator pairing (call rerouted / out-of-jurisdiction)
 *
 * `FETCH_LOCATION_SUCCESS` (and the underlying `addLocToQueue`)
 * unconditionally seeds an entry for the searched callerID even when
 * the LEI service returns nothing useful — at minimum we get
 * `{ isManualSearch: true }`. Without this gate that empty shell shows
 * up as a phantom "Active 911 Call" the next time the user re-runs
 * the same query (e.g. by clicking the recent-search chip).
 */
export function isCallEntryActive(call: EmergencyCall | undefined): boolean {
  if (!call) return false;
  const hasPrimary = (call.core?.locations?.length ?? 0) > 0;
  if (hasPrimary) return true;
  const hasSecondary = (call.core?.secondaryLocations?.length ?? 0) > 0;
  if (hasSecondary) return true;
  if (call.agencyShareChat?.conversationID) return true;
  if (call.collaborator?.conversationID) return true;
  return false;
}

/**
 * Background LEI (Legacy Emergency Info) phone-number lookup, modelled
 * after `useAdrSearch`. Where ADR pulls inline RAD-e via the modern
 * `fetchEmergencySession({ scheme: 'tel' })` channel and surfaces a
 * dropdown row, LEI pulls the legacy
 * `/v1/rsos/location?caller_id=…` endpoint and (on success) selects
 * the caller in the queue so the call pane opens — the same effect
 * the legacy `ConnectedUniversalSearch.searchLocation` had.
 *
 * Both lookups can fire on the same input; LEI is gated to phone
 * shapes only and, by convention, only kicks off when the caller
 * isn't already an active call (the host enforces that with
 * {@link isCallerInQueue}).
 */
export default function useLeiSearch(): UseLeiSearch {
  const dispatch = useDispatch() as unknown as LegacyDispatch;
  const reduxDispatch = useDispatch();
  const store = useStore<RootState>();
  const { productToken } = useSelector(selectProductToken) as {
    productToken?: string;
  };

  const kickoff = useCallback(
    async (callerID: string) => {
      const token =
        productToken ||
        (typeof window !== 'undefined'
          ? (window.localStorage?.getItem('productToken') ?? '')
          : '');
      if (!callerID || !token) return;
      try {
        const res = await dispatch(
          fetchLocation(
            callerID,
            token,
          ) as unknown as Parameters<LegacyDispatch>[0],
        );
        if (res?.action?.type !== FETCH_LOCATION_SUCCESS) return;
        // `fetchLocation` unconditionally seeds the call queue with an
        // entry for `callerID` (via `addLocToQueue` and the
        // `FETCH_LOCATION_SUCCESS` reducer setting `isManualSearch:
        // true`) even when the LEI service returned nothing usable.
        // Inspect the freshly-populated queue entry against the same
        // "real, in-flight call" criterion the rest of the app uses
        // (primary/secondary location or an active conversation) and
        // either hand off to `setSelectedCallerID` or undo the
        // placeholder add. Without this gate the phantom entry shows
        // up as "Active 911 Call" on the next re-search of the same
        // number (e.g. clicking the recent-search chip).
        //
        // Pull the entry through `callQueueSelectorNext` (the canonical
        // map-search view of the queue) rather than the raw slice so
        // any ANI/ALI permission filtering applied for the user is
        // honored — if the entry would be filtered out of the visible
        // queue anyway, we treat it as inactive and clean up.
        const queueSnapshot = callQueueSelectorNext(store.getState()) as
          | Record<string, EmergencyCall>
          | undefined;
        const queueEntry = queueSnapshot?.[callerID];
        if (isCallEntryActive(queueEntry)) {
          reduxDispatch(setSelectedCallerID(callerID));
        } else {
          reduxDispatch(removeNumFromQueue(callerID));
        }
      } catch {
        // The thunk dispatches `fetchLocationFailed` internally, which
        // populates the existing `locationError` slice. The map search
        // bar doesn't have its own LEI-error surface, so we swallow.
      }
    },
    [dispatch, reduxDispatch, store, productToken],
  );

  return useMemo(() => ({ parse: parseLeiTerm, kickoff }), [kickoff]);
}
